import React from 'react';
import { Type } from 'lucide-react';
import {
  useTheme, FONT_SCALES, FONT_SCALE_LABELS, FONT_STACKS, FONT_THEME_LABELS, WRITING_PAGE_LABELS,
  type FontScale, type FontFamilyTheme, type WritingPage,
} from '../../contexts/ThemeContext';
import { TYPE } from '../../constants/typeScale';

/**
 * Display & reading preferences — a global font SIZE scale and a font FAMILY theme, for readability. Applied
 * instantly on <html> (persisted to localStorage via ThemeContext; no server round-trip — it's a device display
 * preference, like theme). Everything is rem-based, so the size scale grows the whole UI proportionally.
 */
const SCALES: FontScale[] = ['sm', 'md', 'lg', 'xl'];
const THEMES: FontFamilyTheme[] = ['default', 'system', 'serif', 'legible'];
const PAGES: WritingPage[] = ['dark', 'paper'];

const DisplaySettings: React.FC = () => {
  const { fontScale, setFontScale, fontFamily, setFontFamily, writingPage, setWritingPage } = useTheme();

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
      <div className="flex items-center gap-2">
        <Type className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Display &amp; reading</h3>
      </div>
      <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
        Set the text size and font that feel most comfortable to read. Applies across the app on this device.
      </p>

      {/* Text size — segmented preset control */}
      <div className="mt-4">
        <span className="text-xs font-medium text-secondary-600 dark:text-secondary-300">Text size</span>
        <div className="mt-1.5 inline-flex flex-wrap gap-1.5">
          {SCALES.map((s) => (
            <button
              key={s}
              onClick={() => setFontScale(s)}
              aria-pressed={fontScale === s}
              className={`min-h-[40px] rounded-lg border px-3 py-2 text-sm transition ${fontScale === s
                ? 'border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-700 font-semibold'
                : 'border-secondary-200 dark:border-secondary-700 text-secondary-600 dark:text-secondary-300 hover:border-secondary-300'}`}
            >
              {FONT_SCALE_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Font theme — each card previews its own family */}
      <div className="mt-5">
        <span className="text-xs font-medium text-secondary-600 dark:text-secondary-300">Font</span>
        <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {THEMES.map((f) => (
            <button
              key={f}
              onClick={() => setFontFamily(f)}
              aria-pressed={fontFamily === f}
              style={{ fontFamily: FONT_STACKS[f] }}
              className={`min-h-[52px] rounded-lg border px-3 py-2 text-left transition ${fontFamily === f
                ? 'border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-700'
                : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-300'}`}
            >
              <div className={`text-sm ${fontFamily === f ? 'font-semibold' : ''} text-secondary-900 dark:text-white`}>{FONT_THEME_LABELS[f]}</div>
              <div className="text-xs text-secondary-500 dark:text-secondary-400">Ag 123</div>
            </button>
          ))}
        </div>
      </div>

      {/* The page the creator writes on (TYPE-3): the workshop stays dark either way. */}
      <div className="mt-5">
        <span className="text-xs font-medium text-secondary-600 dark:text-secondary-300">Writing page</span>
        <p className="mt-0.5 text-xs text-secondary-500 dark:text-secondary-400">
          Where you write your story, series and script. Paper turns that page into a warm sheet; the rest stays dark.
        </p>
        <div className="mt-1.5 inline-flex flex-wrap gap-1.5">
          {PAGES.map((p) => (
            <button
              key={p}
              onClick={() => setWritingPage(p)}
              aria-pressed={writingPage === p}
              data-testid={`writing-page-${p}`}
              className={`min-h-[40px] rounded-lg border px-3 py-2 text-sm transition ${writingPage === p
                ? 'border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-700 font-semibold'
                : 'border-secondary-200 dark:border-secondary-700 text-secondary-600 dark:text-secondary-300 hover:border-secondary-300'}`}
            >
              {WRITING_PAGE_LABELS[p]}
            </button>
          ))}
        </div>
        <div className="ms-page mt-3 rounded-lg border border-secondary-200 p-4 dark:border-secondary-700">
          <p className={`${TYPE.manuscript} text-secondary-900 dark:text-secondary-100`}>
            Mara opens her morning route and finds it addressed to a woman who died yesterday. She delivers it anyway.
          </p>
        </div>
      </div>

      {/* Live preview — reflects the current size + family (renders at the chosen root scale automatically) */}
      <div className="mt-5 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900 p-4">
        <div className="text-[11px] uppercase tracking-wide text-secondary-400 mb-1.5">Preview</div>
        <p className="text-sm leading-relaxed text-secondary-800 dark:text-secondary-200">
          Your specialists will read like this. Clear, comfortable text makes long answers and summaries easier to
          follow — adjust the size and font until it feels right.
        </p>
      </div>

      <p className="mt-3 text-[11px] text-secondary-400">
        Currently: {FONT_SCALE_LABELS[fontScale]} · {FONT_THEME_LABELS[fontFamily]} ({FONT_SCALES[fontScale]}).
      </p>
    </div>
  );
};

export default DisplaySettings;
